/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { IconBack } from '../svg/IconBack'
import { stylesMulti, validateButton } from '../utils/userUtils/FormStyles'
import {
  getDefaultValue,
  getValueForSelect,
  handleMultiSelect,
  handleSelect,
  handleSelectBoolean,
  onEmailChange,
  onInputChange,
} from '../utils/userUtils/EventHandlers'
import { AppStore } from '../store/applicationState'
import { GetRoles } from '../store/admin/roles/actions'
import { GetCategories } from '../store/categories/actions'
import { GetCompanies } from '../store/admin/companies/actions'
import { GetUserById, ResendInvite, UpdateUserInfo } from '../store/admin/users/actions'
import { booleanOptions } from '../utils/userUtils/SelectOptions'
import { FormPhoneInput } from '../components/ui/formUi/FormPhoneInput'
import { InputChecked } from '../svg/InputChecked'
import { InputNotChecked } from '../svg/InputNotChecked'
import { GetMessageMethods } from '../store/messageMethods/actions'
import { ROUTES } from '../utils/routes'
import { getDate } from '../utils/helpers'

interface EditUserFormProps {}

interface CompanyOption {
  value: string
  label: string
  is_approved?: boolean
}

const getCompanyOption = (company: any): CompanyOption => {
  const option = getValueForSelect(company)
  return { ...option, is_approved: company.is_approved }
}

export const EditUserForm: React.FC<EditUserFormProps> = () => {
  const history = useHistory()
  const location = useLocation()
  const { id, userId = +id } = useParams() as any

  const dispatch = useDispatch()
  const { categories, roles, companies, adminUsers, messageMethods, userData } = useSelector((store: AppStore) => store)

  const [firstName, setFirstName] = useState<string>(adminUsers.userForEditData?.first_name || '')
  const [lastName, setLastName] = useState<string>(adminUsers.userForEditData?.last_name || '')
  const [email, setEmail] = useState<string>(adminUsers.userForEditData?.email || '')
  const [tell, setTell] = useState<string>(`${adminUsers.userForEditData?.phone}` || '')
  const [isMobile, setIsMobile] = useState<boolean>(adminUsers.userForEditData?.is_mobile || false)
  const [company, setCompany] = useState<{ value: string; label: string }>(
    adminUsers.userForEditData?.company_id && adminUsers.userForEditData?.company_title
      ? { value: `${adminUsers.userForEditData.company_id}`, label: adminUsers.userForEditData.company_title }
      : { label: '', value: '' }
  )
  const [confirmedDate, setConfirmedDate] = useState<string>(
    getDate(adminUsers.userForEditData?.terms_confirmed_date || '')
  )
  const [termsConfirmed, setTermsConfirmed] = useState<boolean>(adminUsers.userForEditData?.terms_confirmed || false)
  const [lastActive, setLastActive] = useState<string>(getDate(adminUsers.userForEditData?.last_signed_in || ''))
  const [disabled, setDisabled] = useState<boolean>(adminUsers.userForEditData?.disabled || false)
  const [isViewOnly, setIsViewOnly] = useState<boolean>(false)
  const [options, setOptions] = useState(categories.data?.map((item) => getValueForSelect(item)))
  const [messageMethod, setMessageMethod] = useState<{ value: string; label: string } | null>(null)

  const [roleState, setRoleState] = useState<{ value: string; label: string } | null>(null)
  const [categoriesState, setCategoriesState] = useState<{ value: string; label: string }[]>([])
  const [disableEmailNotifications, setDisableEmailNotifications] = useState<boolean>(
    adminUsers.userForEditData?.disable_email_notifications || false
  )

  const [firstNameError, setFirstNameError] = useState<boolean>(false)
  const [lastNameError, setLastNameError] = useState<boolean>(false)
  const [emailError, setEmailError] = useState<boolean>(false)
  const [tellError, setTellError] = useState<boolean>(false)
  const [companyError, setCompanyError] = useState<boolean>(false)
  const [categoriesError, setCategoriesError] = useState(false)
  const [submitError, setSubmitError] = useState<boolean>(false)
  const [isApproved, setIsApproved] = useState<boolean>(true)

  const styles = {
    control: (provided: any) => ({
      ...provided,
      color: 'red',
      padding: '5px',
      border: `${companyError && submitError ? '1px solid rgba(248, 113, 113, 1)' : ''}`,
      boxShadow: 'none',
      '&:hover': {
        borderColor: `${companyError && submitError ? 'rgba(248, 113, 113, 1)' : ''}`,
      },
    }),
  }

  useEffect(() => {
    if (!categories.data) dispatch(GetCategories.request())
    if (!roles.data) dispatch(GetRoles.request())
    dispatch(GetCompanies.request())
    if (!messageMethods.data) dispatch(GetMessageMethods.request())

    dispatch(GetUserById.request({ userId }))
  }, [])

  useEffect(() => {
    if (messageMethods.data && adminUsers.userForEditData?.message_method_id)
      setMessageMethod(getDefaultValue(messageMethods.data, adminUsers.userForEditData.message_method_id))
  }, [messageMethods.data, adminUsers.userForEditData])

  useEffect(() => {
    if (categories.data) setOptions(categories.data.map((item) => getValueForSelect(item)))
  }, [categories.data])

  useEffect(() => {
    if (roles.data && adminUsers.userForEditData) {
      const value = getValueForSelect(roles.data.find((role) => adminUsers.userForEditData?.role_id === role.role_id))
      setRoleState(value.label === '' ? null : value)
    }
  }, [roles.data, adminUsers.userForEditData])

  useEffect(() => {
    if (categories.data && adminUsers.userForEditData) {
      if (adminUsers.userForEditData.full_access_categories.length) {
        setIsViewOnly(false)
        setCategoriesState(
          adminUsers.userForEditData.full_access_categories.map((userFAC) =>
            getValueForSelect(categories.data?.find((category) => userFAC === category.category_id))
          )
        )
      } else {
        setIsViewOnly(true)
        setCategoriesState(
          adminUsers.userForEditData.view_only_categories.map((userVOC) =>
            getValueForSelect(categories.data?.find((category) => userVOC === category.category_id))
          )
        )
      }
    }
  }, [categories.data, adminUsers.userForEditData])

  useEffect(() => {
    if (adminUsers.userForEditData) {
      setFirstName(adminUsers.userForEditData.first_name)
      setLastName(adminUsers.userForEditData.last_name)
      setEmail(adminUsers.userForEditData.email)
      setTell(`${adminUsers.userForEditData.phone}`)
      setIsMobile(adminUsers.userForEditData.is_mobile)
      setCompany({
        value: `${adminUsers.userForEditData.company_id}`,
        label: adminUsers.userForEditData.company_title,
      })
      setTermsConfirmed(adminUsers.userForEditData.terms_confirmed)
      setLastActive(getDate(adminUsers.userForEditData.last_signed_in))
      setDisabled(adminUsers.userForEditData.disabled)
      setConfirmedDate(getDate(adminUsers.userForEditData.terms_confirmed_date))
      setDisableEmailNotifications(adminUsers.userForEditData.disable_email_notifications || false)
    }
  }, [adminUsers.userForEditData])

  const onSubmitUserForm = (event: any) => {
    event.preventDefault()
    if (emailError || firstNameError || lastNameError || tellError || companyError || categoriesError) {
      setSubmitError(true)
      return null
    }
    setSubmitError(false)
    if (!companies.data || !adminUsers.userForEditData) return null
    return dispatch(
      UpdateUserInfo.request({
        user_id: userId,
        body: {
          company_id: +company.value,
          disabled,
          email,
          first_name: firstName,
          full_access_categories: !isViewOnly ? categoriesState.map((item) => +item.value) : [],
          is_mobile: isMobile,
          last_name: lastName,
          message_method_id: messageMethod?.value ? +messageMethod.value : undefined,
          phone: tell,
          role_id: roleState?.value ? +roleState.value : undefined,
          terms_confirmed: termsConfirmed,
          view_only_categories: isViewOnly ? categoriesState.map((item) => +item.value) : undefined,
          disable_email_notifications: disableEmailNotifications,
        },
      })
    )
  }

  const onResendInvite = () => dispatch(ResendInvite.request({ idUser: userId }))

  const onCompanySelect = (selectedOption: CompanyOption | null) => {
    if (selectedOption) {
      setCompany({ value: selectedOption.value, label: selectedOption.label })
      setCompanyError(false)
      const approved = selectedOption.is_approved || false
      setIsApproved(approved)
      setIsViewOnly(!approved)
    }
  }

  return (
    <div className='pb-3 bg-white'>
      <button
        type='button'
        className='py-1.5 smallMobile:py-3 w-max flex flex-row items-center h-16'
        onClick={() => {
          // @ts-ignore
          if (location.state?.fromUsers) history.goBack()
          else history.push(`${ROUTES.adminPageUsersCustomers}`)
        }}
      >
        <IconBack />
        <div className='ml-2 text-menuLink opacity-50 hover:opacity-80'>Back to table</div>
      </button>

      <div className='flex justify-center items-center'>
        <form
          noValidate
          onSubmit={(event) => onSubmitUserForm(event)}
          action=''
          className='2smallest:px-2 smallMobile:px-4 w-11/12 max-w-screen-xl'
        >
          <div className='flex flex-col lg:flex-row'>
            <div className='form-user-block lg:mr-2'>
              <div className='font-circa text-2xl font-normal'>Identity</div>
              <div className='mb-1 p-3 bg-bluegray-50 flex flex-col md:flex-row justify-between'>
                <div className='md:w-1/2 md:pr-1 mb-3 md:mb-0'>
                  <label htmlFor='firstName' className='form-label'>
                    First name *
                  </label>
                  <div>
                    <input
                      value={firstName}
                      onChange={(event) => onInputChange(event, setFirstName, setFirstNameError)}
                      type='text'
                      name='firstName'
                      id='firstName'
                      placeholder='Enter First name *'
                      className={`form-input ${validateButton(firstNameError, submitError)}`}
                    />
                    <div className={`form-error-text mt-1 ${firstNameError && submitError ? 'block' : 'hidden'}`}>
                      Please enter a valid first name.
                    </div>
                  </div>
                </div>

                <div className='md:w-1/2 md:pl-1'>
                  <label htmlFor='lastName' className='form-label'>
                    Last name *
                  </label>
                  <div>
                    <input
                      value={lastName}
                      onChange={(event) => onInputChange(event, setLastName, setLastNameError)}
                      type='text'
                      name='lastName'
                      id='lastName'
                      placeholder='Enter Last name *'
                      className={`form-input ${validateButton(lastNameError, submitError)}`}
                    />
                    <div className={`form-error-text mt-1 ${lastNameError && submitError ? 'block' : 'hidden'}`}>
                      Please enter a valid last name.
                    </div>
                  </div>
                </div>
              </div>
              <div className='mb-1 p-3 bg-bluegray-50 flex flex-col flex-wrap md:flex-row justify-between'>
                <div className='md:w-2/3 md:pr-1 mb-3 md:mb-0'>
                  <label htmlFor='email' className='form-label'>
                    Email *
                  </label>
                  <div>
                    <input
                      value={email}
                      onChange={(event) => onEmailChange(event, setEmail, setEmailError)}
                      type='email'
                      name='email'
                      id='email'
                      placeholder='Enter Email *'
                      className={`form-input ${validateButton(emailError, submitError)}`}
                    />
                    <div className={`form-error-text mt-1 ${emailError && submitError ? 'block' : 'hidden'}`}>
                      Please enter valid email address.
                    </div>
                  </div>
                </div>

                <div className='md:w-1/3 md:pl-1'>
                  <label className='form-label'>Resend invite</label>
                  <button
                    type='button'
                    className='bg-lightblue-600 text-white w-full h-12 rounded-md product-buttons'
                    onClick={onResendInvite}
                  >
                    Resend invite
                  </button>
                </div>

                <label
                  htmlFor='disableEmailNotifications'
                  className={`w-full text-sm text-gray-600 flex items-center mt-4 cursor-pointer`}
                >
                  <input
                    type='checkbox'
                    id='disableEmailNotifications'
                    className='hidden'
                    checked={disableEmailNotifications}
                    onChange={() => setDisableEmailNotifications(!disableEmailNotifications)}
                  />
                  {disableEmailNotifications ? <InputChecked /> : <InputNotChecked />}
                  <span className='ml-3 select-none'>Do not send email notifications</span>
                </label>
              </div>

              <div className='mb-1 p-3 bg-bluegray-50 flex flex-col md:flex-row justify-between'>
                <div className='md:w-2/3 md:pr-1 mb-3 md:mb-0'>
                  <label htmlFor='phoneNumber' className='form-label'>
                    Phone number *
                  </label>
                  <FormPhoneInput
                    userTell={tell}
                    setUserTell={setTell}
                    userTellError={tellError}
                    setTellError={setTellError}
                    submitError={submitError}
                  />
                </div>
                <div className='md:w-1/3 md:pl-1'>
                  <label className='form-label'>Message method</label>
                  {messageMethods.data && (
                    <Select
                      name='messageMethods'
                      isSearchable
                      options={messageMethods.data.map((item) => getValueForSelect(item))}
                      value={messageMethod}
                      onChange={(event) => handleSelect(event, setMessageMethod, () => {})}
                      styles={stylesMulti}
                      id='messageMethods'
                    />
                  )}
                </div>
              </div>
              <div className='mb-1 p-3 bg-bluegray-50'>
                <label
                  htmlFor='isMobile'
                  className='w-max-content text-sm text-gray-600 flex items-center cursor-pointer'
                >
                  <input type='checkbox' id='isMobile' className='hidden' onClick={() => setIsMobile(!isMobile)} />
                  {isMobile ? <InputChecked /> : <InputNotChecked />}
                  <span className='ml-3 select-none'>Is mobile</span>
                </label>
              </div>
              <div className='mb-1 p-3 bg-bluegray-50'>
                <label htmlFor='company' className='form-label'>
                  Company *
                </label>
                <div>
                  <Select
                    options={companies.data?.companies?.map((item) => getCompanyOption(item))}
                    isClearable={false}
                    placeholder='Select company'
                    menuPlacement='top'
                    value={getValueForSelect(company)}
                    onChange={(event) => onCompanySelect(event)}
                    styles={styles}
                    id='company'
                  />
                  <div className={`form-error-text mt-1 ${companyError && submitError ? 'block' : 'hidden'}`}>
                    Please select or enter your company.
                  </div>
                </div>
              </div>
            </div>
            <div className='form-user-block mt-6 lg:mt-0 lg:ml-2 lg:h-3/4 min-h-[528px]'>
              <div className='font-circa text-2xl font-normal'>Status</div>
              <div className='mb-1 p-3 bg-bluegray-50 flex flex-col md:flex-row justify-between'>
                <div className='md:w-2/3 md:pr-1 mb-3 md:mb-0'>
                  <label htmlFor='phoneNumber' className='form-label'>
                    Terms confirmed date
                  </label>
                  <input
                    value={confirmedDate}
                    disabled
                    type='text'
                    name='confirmedData'
                    id='confirmedData'
                    className='form-input disabled:bg-gray-200'
                  />
                </div>
                <div className='md:w-1/3 md:pl-1'>
                  <label className='form-label'>Terms confirmed</label>
                  <div>
                    <Select
                      options={booleanOptions}
                      value={getValueForSelect(`${termsConfirmed}`)}
                      onChange={(event) => handleSelectBoolean(event, setTermsConfirmed)}
                      isSearchable={false}
                      styles={stylesMulti}
                      name='termsConfirmed'
                      id='termsConfirmed'
                    />
                  </div>
                </div>
              </div>
              <div className='mb-1 p-3 bg-bluegray-50 flex flex-col md:flex-row justify-between'>
                <div className='md:w-2/3 md:pr-1 mb-3 md:mb-0'>
                  <label htmlFor='phoneNumber' className='form-label'>
                    Last Sign in Time
                  </label>
                  <input
                    value={lastActive}
                    disabled
                    type='text'
                    name='lastActive'
                    id='lastActive'
                    className='form-input disabled:bg-gray-200'
                  />
                </div>
                <div className='md:w-1/3 md:pl-1'>
                  <label className='form-label'>Disabled</label>
                  <div>
                    <Select
                      options={booleanOptions}
                      value={getValueForSelect(`${disabled}`)}
                      onChange={(event) => handleSelectBoolean(event, setDisabled)}
                      isSearchable={false}
                      styles={stylesMulti}
                      name='Disabled'
                      id='Disabled'
                    />
                  </div>
                </div>
              </div>
              <div className='mb-1 p-3 bg-bluegray-50'>
                <label htmlFor='role' className='form-label'>
                  Role
                </label>
                <div>
                  <Select
                    options={roles.data
                      ?.filter((item) => (userData.isAssociateAdmin ? item.role_id !== 1 : item))
                      .map((item) => getValueForSelect(item))}
                    value={roleState || null}
                    placeholder='Select user role'
                    onChange={(event) => handleSelect(event, setRoleState, () => {})}
                    styles={stylesMulti}
                    isClearable
                    id='role'
                  />
                </div>
              </div>
              <div className={`mb-1 p-3 bg-bluegray-50 ${!isApproved ? 'pb-0' : ''}`}>
                <label htmlFor='full_access_categories' className='form-label'>
                  Categories*
                </label>
                <div>
                  <Select
                    options={options}
                    closeMenuOnSelect={false}
                    placeholder='Select user categories'
                    value={categoriesState}
                    onChange={(event) => handleMultiSelect(event, setCategoriesState, setCategoriesError)}
                    styles={stylesMulti}
                    isMulti
                    isClearable
                    id='full_access_categories'
                  />
                </div>
                <div className={`form-error-text mt-1 ${categoriesError && submitError ? 'block' : 'hidden'}`}>
                  Selecting at least one category is required.
                </div>
                <label
                  htmlFor='isViewOnly'
                  className={`w-max-content text-sm text-gray-600 flex items-center mt-4 cursor-pointer ${
                    !isApproved ? 'cursor-default opacity-50' : ''
                  }`}
                >
                  <input
                    type='checkbox'
                    id='isViewOnly'
                    className='hidden'
                    checked={isViewOnly}
                    disabled={!isApproved}
                    onChange={() => setIsViewOnly(!isViewOnly)}
                  />
                  {isViewOnly ? <InputChecked /> : <InputNotChecked />}
                  <span className='ml-3 select-none'>View only</span>
                </label>
                {!isApproved && (
                  <p className='text-red-500 mt-1 text-sm'>User can have full access only if the company is approved</p>
                )}
              </div>
            </div>
          </div>
          <div className='p-3 mt-6 flex items-center justify-center'>
            <button
              type='submit'
              className='py-3 px-16 text-white bg-green-700 hover:bg-green-800 text-base rounded-md product-buttons'
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
